import fastdom from 'fastdom';
import Chart from './chart';
import cssClasses from '../base/css-classes';
import { validateData, normalizeData } from '../helpers/data-format';
import * as utils from '../helpers/utils';
import * as dateLabels from '../helpers/date-labels';
import * as nonconcurrentAF from '../helpers/nonconcurrent-raf';
import * as overlay from '../helpers/overlay';
import * as stylesHelper from '../helpers/styles';
import * as datesHelper from '../helpers/dates';
import COMPONENT_CSS from './line-chart.less';
import {
  SVG,
  EmbeddedSVG,
  Rect,
  Line,
  Circle,
  Group,
  Text,
  Path,
  ClipPath,
} from '../helpers/svg';

/*
 ** Constants
 */

// Chart modes
const MODE_ARTICLE = 'article';
const MODE_TILE = 'tile';
const MODE_INTERACTIVE = 'interactive';
const MODE_CUSTOM = 'custom';
const DEFAULT_OPTIONS = {
  mode: MODE_ARTICLE,
  width: '100%',
  height: '100%',
  showHeader: true,
  showFooter: true,
  showLegend: true,
  yAxisPosition: 'right',
  showPointLabels: true,
  pointLabels: {},
  dateFormat: 'day',
  ticks: null,
  zoomLevel: 1,
  x_axis_min: null,
  x_axis_max: null,
  y_axis_min: null,
  y_axis_max: null,
  y_precision: null,
  unitFormat: null,
  embedStylesheet: true,
  displayTooltips: true,
  strictModeTooltips: false,
  wrapAxisLabels: false,
  enableValidation: false,
  strictValidation: false,
  forceMultilineLegend: false,
  tooltipLabel: null,
  tooltipLineLabel: null,
  groupTooltipLabel: null,
};
const KINETIC = 325; // milliseconds

// Stylistic
const BASE_PADDING = 10;
const APPROX_NUM_TICKS = 5;
const APPROX_NUM_TICKS_INTERACTIVE = 3;
const LABEL_SPACING = 10;
const LABEL_SPACING_INTERACTIVE = 30;
const MIN_X_AXIS_OFFSET = 20;
const X_AXIS_TICK_MARK_HEIGHT = 10;
const FONT_STACK =
  '"aktiv-grotesk", "aktiv-grotesk-std", Arial, Helvetica, sans-serif';
const TITLE_FONT = `18px ${FONT_STACK}`;
const SM_TEXT_FONT = `11px ${FONT_STACK}`;
const TITLE_LEADING = 1.3;
const SM_TEXT_LEADING = 1.3;
const LEGEND_MARKER_SIZE = 14;
const DATA_POINT_RADIUS = 5;
const ANNOTATION_MAX_WIDTH = 150;

// Article Mode
const ARTICLE_MODE_CHART_WIDTH = 720;
const ARTICLE_MODE_PLOT_AREA_HEIGHT = 175;
const ARTICLE_MODE_TEXT_WRAP = 530;
const ARTICLE_MODE_MAX_LINES = 4;

// Tile Mode
const CHART_WIDTH_TILE = 300;
const CHART_HEIGHT_TILE = 300;

// rAF update IDs
const RESIZE_UPDATE = 'resize_update';
const SCROLL_UPDATE = 'scroll_update';
const TOOLTIP_UPDATE = 'tooltip_update';
const PANNING_UPDATE = 'panning_update';

/*
 ** Private Variables
 */

const domWrite = fastdom.mutate.bind(fastdom);
const activeInstances = [];
const supports3dTransforms = utils.supportsCssProperty('perspective');
let dragTicker;

/*
 ** Helper Functions
 */

/** Checks whether the given LineChart instance is in one of the given modes
 * @param {LineChart} instance - The LineChart instance
 * @param {...String} modes - The mode to test for (MODE_ARTICLE, etc)
 * @returns {Boolean} - Whether the LineChart instance is in one of the given modes
 */
function modeIs(instance, ...modes) {
  return modes.some((mode) => instance.options.mode === mode);
}

/** Gets the left position (in pixels) for a given x value
 * @param {LineChart} instance - The LineChart instance
 * @param {Number} x - The x value to plot
 * @returns {Number} - Left (or x) position in pixels
 */
function plotX(instance, x) {
  const scalingFactor =
    instance.dom.plotArea.width() / (instance.metadata.axisX.axisRange || 1);
  return utils.roundPixels(
    scalingFactor * (x - instance.metadata.axisX.axisMin)
  );
}

/** Gets the top position (in pixels) for a given y value
 * @param {LineChart} instance - The LineChart instance
 * @param {Number} y - The y value to plot
 * @returns {Number} - Top (or y) position in pixels
 */
function plotY(instance, y) {
  const h = instance.dom.plotArea.height();
  const scalingFactor = h / instance.metadata.axisY.axisRange;
  return utils.roundPixels(
    h - scalingFactor * (y - instance.metadata.axisY.axisMin)
  );
}

/** Gets nearest x value for a given left position (in pixels) if there is one
 * @param {LineChart} instance - The LineChart instance
 * @param {Number} posX - The left position relative to the chart (e.g. from a mouse event)
 * @param {Number} lineIndex - Index of the line to search along (for perf optimization)
 * @returns {Number} - Nearest x value
 */
function findX(instance, posX, lineIndex = -1) {
  const { axisX } = instance.metadata;
  const pool = axisX.valuesByLine[lineIndex] || axisX.values;
  const scalingFactor = axisX.axisRange / instance.dom.plotArea.width();
  const descaled = posX * scalingFactor + axisX.axisMin;

  // Check for an exact match
  if (pool.indexOf(descaled) > -1) {
    return descaled;
  }

  // Otherwise return the closest match
  let closest = pool[0];
  let closestDiff = Math.abs(closest - descaled);
  for (let i = 1; i < pool.length; i++) {
    const diff = Math.abs(pool[i] - descaled);
    if (diff < closestDiff) {
      closest = pool[i];
      closestDiff = diff;
    }
  }
  return closest;
}

/** Gets pixel position based on an index value
 * @param {LineChart} instance - The LineChart instance
 * @param {Number} posX - The index of the chart value needed
 * @returns {Number} - Scaled client X value
 */
function findClientX(instance, posX) {
  const { axisX } = instance.metadata;
  const scalingFactor = axisX.axisRange / instance.dom.plotArea.width();
  const scaled = (posX - axisX.axisMin) / scalingFactor;

  return scaled + instance.state.plotAreaOffset.left;
}

/** Gets the interval between tick marks for a given range of data
 * @param {Number} range - The range of the data (maxY - minY)
 * @param {Boolean} userDefinedScale - If a set min or max Y-axis value has been set
 * @param {Number} preferredNumberOfTicks - Number of tick marks to try for
 * @returns {Number} - Interval between tick marks
 */
function getTickIncrement(range, userDefinedScale, preferredNumberOfTicks) {
  const step = range / preferredNumberOfTicks;
  const magnitude = utils.orderOfMagnitude(step);

  const residual = step / magnitude;
  let increment;
  if (residual > 5) {
    increment = 10 * magnitude;
  } else if (residual > 2) {
    increment = 5 * magnitude;
  } else if (residual > 1) {
    increment = 2 * magnitude;
  } else {
    increment = magnitude;
  }

  // If the user has set an explicit scale, the range
  // must be evenly divisible by the increment
  if (userDefinedScale && range % increment !== 0) {
    // Since we couldn't find a "nice" increment value, we must resort to using the
    // quotient of range divided by ticks as the increment value. Before we do this,
    // though, let's see if reducing the number of ticks by 1 would help.
    const altStep = range / (preferredNumberOfTicks - 1);
    if (utils.decimalPlaces(step) > utils.decimalPlaces(altStep)) {
      increment = altStep;
    } else {
      increment = step;
    }
  }

  return increment;
}

/** Gets y-axis tick marks locations and labels
 * @param {Number} min - The minimum y value
 * @param {Number} max - The maximum y value
 * @param {Number} numTicks - Number of tick marks to try for
 * @param {Number} setMin - The y axis minimum if set
 * @param {Number} setMax - The y axis maximum if set
 * @returns {Object[]} - Tick marks in { value, text } format
 */
function getAxisTicks(min, max, numTicks, setMin = null, setMax = null) {
  const threshold = (max - min) * 0.08; // +/- 8% just happens to work well
  const fixedMin = setMin !== null;
  const fixedMax = setMax !== null;
  let axisMin = fixedMin ? setMin : min - threshold;
  let axisMax = Math.max(fixedMax ? setMax : max + threshold, max + threshold);

  if (axisMin === axisMax) {
    const mult = Math.max(utils.orderOfMagnitude(axisMin), 1);
    axisMin = fixedMin ? setMin : axisMin - mult * 2;
    axisMax = fixedMax ? setMax : axisMax + mult * 2;
  } else if (axisMax < axisMin) {
    // Swap values
    [axisMin, axisMax] = [axisMax, axisMin];
  }

  const axisRange = axisMax - axisMin;
  const increment = getTickIncrement(axisRange, fixedMin && fixedMax, numTicks);
  const firstTick = fixedMin
    ? axisMin
    : utils.floorToMultiple(axisMin, increment);
  const lastTick = fixedMax
    ? axisMax
    : utils.ceilToMultiple(axisMax, increment);
  const precision = utils.decimalPlaces(increment);
  const ticks = [];

  for (let value = firstTick; value <= lastTick; value += increment) {
    ticks.push({
      value,
      text: utils.readableNumber(value, precision, true),
    });
  }

  return ticks;
}

/** Gets x-axis tick marks locations and labels
 * @param {LineChart} instance - The LineChart instance
 * @returns {Object[]} - Tick marks in { x, text } format
 */
function getXAxisTicks(instance) {
  const { data, state } = instance;
  const { axisX, axisY } = instance.metadata;
  const ticks = [];
  const tickLabels = axisX.labels || data.labels;

  if (modeIs(instance, MODE_TILE)) {
    ticks.push(tickLabels[0]);
    ticks.push(tickLabels[tickLabels.length - 1]);
    return ticks;
  }

  const estPlotAreaWidth =
    state.innerWidth - axisY.width - BASE_PADDING - axisX.plotOffset * 2;
  const maxTickCount =
    (estPlotAreaWidth * state.zoomLevel) /
    (axisX.maxLabelWidth + state.labelSpacing);
  let increment;
  let lastLabel;

  if (axisX.labels) {
    lastLabel = axisX.labels.length - 1;
    increment = Math.ceil(axisX.labels.length / maxTickCount);
  } else {
    lastLabel = Math.min(data.labels.length - 1, axisX.max);
    increment = Math.max(Math.ceil(data.labels.length / maxTickCount), 1);
  }

  const firstTick = lastLabel % increment;
  for (let i = firstTick; i <= lastLabel; i += increment) {
    ticks.push(tickLabels[i]);
  }

  return ticks;
}

/** Reformats date range label (e.g. "2018 Jan 1-2018 Jan 3" -> "2018 Jan 1–3")
 * @param {String} labelText
 * @returns {String} - Reformatted date range label
 */
function getDateRangeTooltipLabel(labelText) {
  const dates = labelText.split('-');
  if (dates.length < 2) return labelText;

  const dateParts = dates.map((d) => d.split(' '));
  let end;
  if (dateParts[0][0] !== dateParts[1][0]) {
    end = dateParts[1].join(' ');
  } else if (dateParts[0][1] !== dateParts[1][1]) {
    end = dateParts[1][1] + dateParts[1][2];
  } else {
    end = dateParts[1][2];
  }

  return `${dates[0]}&ndash;${end}`;
}

/** Gets the corresponding x-axis label for a given x value
 * @param {LineChart} instance - The LineChart instance
 * @param {Number} x - The x value
 * @returns {String} - The x-axis label for the given x value
 */
function getLabelForX(instance, x) {
  const label = instance.data.labelsLookup[x];
  if (label) {
    return instance.data.labelType === 'date_range'
      ? getDateRangeTooltipLabel(label)
      : label;
  }
  return x.toString();
}

/*
 ** LineChart Definition
 */

class LineChart extends Chart {
  /** Creates a new LineChart instance
   * @param {HTMLElement} container - DOM element to mount LineChart to
   * @param {Object} data - Data set object containing labels and lines
   * @param {Object} options - Options object
   */
  constructor(container, data, options = {}) {
    super();
    this.container =
      typeof container === 'string'
        ? document.querySelector(container)
        : container;
    this.options = Object.assign({}, DEFAULT_OPTIONS, options);
    this.dom = {};
    this.metadata = {};
    this.state = {};

    this.zoomFadeId = 'zoom-fade-' + (document.querySelectorAll("[id*='zoom-fade']").length + 1);
  
    // Create root <svg> element
    this.dom.chart = new SVG().appendTo(this.container);
    this.el = this.dom.chart.node;
    // Store reference to chart instance on root svg element for reverse lookup
    this.el.dvInstance = this;

    // Register event listeners
    this.on('mouseenter', this.mouseEnter.bind(this))
      .on('mousedown', this.mouseDown.bind(this))
      .on('touchstart', this.touchStart.bind(this))
      .on('mousemove', this.mouseMove.bind(this))
      .on('touchmove', this.touchMove.bind(this))
      .on('mouseup', this.mouseUp.bind(this))
      .on('touchend', this.touchEnd.bind(this))
      .on('mouseleave', this.mouseLeave.bind(this))
      // Prevent Firefox's default drag behavior on SVG elements -- https://bugzilla.mozilla.org/show_bug.cgi?id=525591
      .on('dragstart', (e) => e.preventDefault())
      .on('focus', this.focusStart.bind(this))
      .on('blur', this.focusEnd.bind(this))
      .on('keydown', this.keyDown.bind(this));

    if (options.enableValidation) {
      const isValid = validateData(data).valid;
      if (!isValid && options.strictValidation) return;
    }

    const chartCodeName = (this.container.parentElement.dataset.codename === 'approvalFlexChart') ? true : false;
    this.data = normalizeData(data, chartCodeName);

    this.init();
  }

  /** Mounts chart element to container node and configures chart based on options object
   * @returns {LineChart} - The chart instance
   */
  init() {
    const { options, dom, state } = this;

    // Make sure root svg is attached to container -- may have just destroyed
    if (!this.el.parentNode) {
      this.container.appendChild(this.el);
    }

    // Add LineChart class and apply chart mode class
    dom.chart.attr(
      'class',
      `${cssClasses.line_chart} dv-${this.options.mode}-mode`
    );
    
    // Set state vars based on options
    state.chartPadding = !modeIs(this, MODE_INTERACTIVE)
      ? BASE_PADDING * 2
      : BASE_PADDING;
    state.chartTicks = !modeIs(this, MODE_INTERACTIVE)
      ? APPROX_NUM_TICKS
      : APPROX_NUM_TICKS_INTERACTIVE;
    state.maxLines = Infinity;
    state.tooltipsEnabled = options.displayTooltips;
    state.headerShown = options.showHeader && !modeIs(this, MODE_INTERACTIVE);
    state.zoomLevel = modeIs(this, MODE_INTERACTIVE, MODE_CUSTOM)
      ? options.zoomLevel
      : 1;
    state.labelSpacing = modeIs(this, MODE_INTERACTIVE)
      ? LABEL_SPACING_INTERACTIVE
      : LABEL_SPACING;

    switch (options.mode) {
      case MODE_ARTICLE:
        // In article mode, height is based on content
        // but we'll set 350 as an initial height
        dom.chart.size(ARTICLE_MODE_CHART_WIDTH, 350);
        state.maxLines = ARTICLE_MODE_MAX_LINES;
        break;
      case MODE_TILE:
        dom.chart.size(CHART_WIDTH_TILE, CHART_HEIGHT_TILE);
        break;
      case MODE_INTERACTIVE:
      case MODE_CUSTOM:
      default:
        dom.chart.size(options.width, options.height);
    }
    state.svgRect = this.el.getBoundingClientRect();
    state.innerWidth = state.svgRect.width - state.chartPadding * 2;
    state.innerHeight = state.svgRect.height - state.chartPadding * 2;
    state.textWrap = modeIs(this, MODE_ARTICLE)
      ? ARTICLE_MODE_TEXT_WRAP
      : state.innerWidth;

    if (state.zoomLevel > 1) {
      dom.chart.addClass(cssClasses.chart_zoomed);
    }

    if (options.embedStylesheet) {
      this.embedStyles();
    } else {
      stylesHelper.add(COMPONENT_CSS);
    }

    // Draw chart
    this.createMetadata().draw().emit('init');

    activeInstances.push(this);
    return this;
  }

  /** Gathers metadata about the data object, calculates tick marks, etc
   * @returns {LineChart} - The chart instance
   */
  createMetadata() {
    const { data, options, dom, state } = this;
    // Initialize some default values (this is mostly in case we weren't
    // given labels or lines arrays so we can still render a blank graph)
    this.metadata = {
      axisX: {
        height: 0,
        plotOffset: MIN_X_AXIS_OFFSET,
      },
      axisY: {
        width: 0,
      },
    };
    if (!data.lines.length && !data.labels.length) return this;
    const { axisX, axisY } = this.metadata;

    // Enforce max lines limits
    data.lines = data.lines.slice(0, state.maxLines);

    if (data.labelType === 'date' || data.labelType === 'date_range') {
      axisX.labels = dateLabels.formatAll(data.labels, options.dateFormat);
    } else if (data.labelType === 'number') {
      axisX.labels = [];
      for (
        let { x } = data.labels[0];
        x <= data.labels[data.labels.length - 1].x;
        x++
      ) {
        axisX.labels.push({ x, text: x.toString() });
      }
    }

    const pointsByLine = data.lines.map((line) => line.points);
    let points = pointsByLine.reduce((a, b) => a.concat(b), []);
    if (!points.length && data.labels.length) {
      points = data.labels.map((label) => ({ x: label.x, y: 0 }));
    }

    // X-axis metadata
    axisX.values = points
      .map((point) => point.x)
      .filter(utils.isFirstOccurrence)
      .sort((a, b) => a - b);
    axisX.valuesByLine = pointsByLine.map((line) =>
      line.map((point) => point.x)
    );
    axisX.min = axisX.values[0];
    axisX.max = axisX.values[axisX.values.length - 1];
    axisX.range = axisX.max - axisX.min;

    if (!data.labels.length) {
      if (axisX.values.length) {
        data.labels = axisX.values.map((x) => ({ x, text: x.toString() }));
      } else {
        return this;
      }
    }
    const labels = axisX.labels || data.labels;

    axisX.axisMin =
      options.x_axis_min !== null
        ? options.x_axis_min
        : Math.min(axisX.min, labels[0].x);
    axisX.axisMax =
      options.x_axis_max !== null
        ? options.x_axis_max
        : Math.max(axisX.max, labels[labels.length - 1].x);
    axisX.axisRange = axisX.axisMax - axisX.axisMin;

    if (options.ticks && options.ticks.length) {
      if (!isNaN(options.ticks[0])) {
        axisX.ticks = options.ticks
          .filter((val, i, arr) => arr.indexOf(val) === i)
          .map((x) => ({
            x,
            text: data.labels[x] ? data.labels[x].text : x.toString(),
          }));
      } else if (
        dateLabels.parse(options.ticks[0]) &&
        (data.labelType === 'date' || data.labelType === 'date_range')
      ) {
        const first = datesHelper.dateFromEpochDays(axisX.min);
        const last = datesHelper.dateFromEpochDays(axisX.max);
        axisX.ticks = options.ticks
          .map((text) =>
            dateLabels.format(
              dateLabels.parse(text),
              options.dateFormat,
              first,
              last
            )
          )
          .filter((val, i, arr) => arr.indexOf(val) === i);
      } else if (typeof options.ticks[0] === 'string') {
        axisX.ticks = options.ticks
          .filter((val, i, arr) => arr.indexOf(val) === i)
          .map((val) => data.labels.find((el) => el.text === val))
          .filter((val) => !!val);
      } else {
        axisX.ticks = options.ticks;
      }

      axisX.ticks.sort(utils.compareX);
    }

    // Y-axis metadata
    axisY.values = points
      .map((point) => point.y)
      .filter(utils.isFirstOccurrence)
      .sort((a, b) => a - b);
    axisY.valuesByLine = pointsByLine.map((line) =>
      line.map((point) => point.y)
    );
    axisY.min = axisY.values[0];
    axisY.max = axisY.values[axisY.values.length - 1];
    axisY.decimalPlaces = Math.max(
      ...axisY.values.map((val) => utils.decimalPlaces(val))
    );

    if (
      options.y_axis_min !== null &&
      options.y_axis_max !== null &&
      options.y_axis_min === options.y_axis_max
    ) {
      throw new Error('Y-axis range cannot be zero.');
    }

    // Get y-axis ticks
    axisY.ticks = getAxisTicks(
      axisY.min,
      axisY.max,
      state.chartTicks,
      options.y_axis_min,
      options.y_axis_max
    ).map((t) => ({
      y: t.value,
      text: options.unitFormat
        ? options.unitFormat.replace('{y}', t.text)
        : t.text,
      hideLabel:
        options.y_precision !== null &&
        Number(t.value.toFixed(options.y_precision)) !== t.value,
    }));

    // Determine min and max y-axis values
    axisY.axisMin = axisY.ticks[0].y;
    axisY.axisMax = axisY.ticks[axisY.ticks.length - 1].y;
    axisY.axisRange = axisY.axisMax - axisY.axisMin;

    // Calculate widest y-axis label
    if (options.yAxisPosition !== 'hidden') {
      const labelStrings = axisY.ticks
        .filter((label) => !label.hideLabel)
        .map((tick) => tick.text);
      axisY.width = utils.measureWidestText(labelStrings, SM_TEXT_FONT);
    }

    // Calculate widest x-axis label
    {
      const labelStrings = (axisX.ticks || labels).map((label) =>
        label.text.toUpperCase()
      );
      axisX.maxLabelWidth = utils.measureWidestText(labelStrings, SM_TEXT_FONT);

      axisX.ticks = axisX.ticks || getXAxisTicks(this);
      axisX.maxShownLabelWidth = utils.measureWidestText(
        axisX.ticks.map((t) => t.text.toUpperCase()),
        SM_TEXT_FONT
      );

      if (modeIs(this, MODE_INTERACTIVE)) {
        axisX.plotOffset = DATA_POINT_RADIUS;
      } else {
        axisX.plotOffset = Math.max(
          Math.ceil(axisX.maxShownLabelWidth / 2) + 2,
          MIN_X_AXIS_OFFSET
        );
      }
    }

    // Estimate x-axis height
    {
      const el = new EmbeddedSVG()
        .appendTo(dom.chart)
        .addClass(cssClasses.x_axis);

      if (!modeIs(this, MODE_INTERACTIVE)) {
        el.appendRawSVG(`
          <g class="${cssClasses.tick}" transform="translate(0, 0)">
            <line y2="${X_AXIS_TICK_MARK_HEIGHT}"/>
            <text y="${
              X_AXIS_TICK_MARK_HEIGHT + BASE_PADDING * 1.5
            }">Example</text>
          </g>
        `);
      } else {
        el.appendRawSVG(`
          <g class="${cssClasses.tick}" transform="translate(0, 0)">
            <line y2="1"/>
            <text y="${
              BASE_PADDING * (options.wrapAxisLabels ? 3 : 1.5)
            }">Example</text>
          </g>
        `);
      }

      axisX.height = el.bbox().height;
      el.remove();
    }

    return this;
  }

  /** Renders all of the chart components (plot area, legend, header, footer, etc)
   * @returns {LineChart} - The chart instance
   */
  draw() {
    const { options } = this;

    // Draw chart sections
    this.drawHeader()
      .drawLegend()
      .drawFooter()
      .drawPlotArea()
      .drawXAxis()
      .drawYAxis()
      .drawLines()
      .layout();

    if (options.displayTooltips) {
      this.drawTooltips();
    }

    this.emit('draw');
    return this;
  }

  layout() {
    const { metadata, dom, state, options } = this;
    const padding = state.chartPadding;
    let refX = padding;
    let refY = padding;

    // chart header
    if (dom.header) {
      dom.header.x(padding).y(padding);
      refY = dom.header.y() + dom.header.height() + BASE_PADDING * 2;
    }

    // legend
    if (dom.legend) {
      dom.legend.x(padding).y(refY);
      refY = dom.legend.y() + dom.legend.height() + BASE_PADDING * 1.5;
    }

    // y-axis
    dom.axisY.y(refY);
    if (options.yAxisPosition === 'hidden') {
      dom.axisY.hide();
    } else if (options.yAxisPosition === 'left') {
      dom.axisY.x(padding);
      refX = dom.axisY.x();

      if (!modeIs(this, MODE_INTERACTIVE)) {
        refX += metadata.axisY.width + BASE_PADDING;
      }
    } else {
      dom.axisY.x(
        dom.plotBackground.width() +
          padding +
          (!modeIs(this, MODE_INTERACTIVE)
            ? BASE_PADDING
            : -metadata.axisY.width)
      );
    }

    // plot background
    dom.plotBackground.x(refX).y(refY);
    refX = dom.plotBackground.x();
    refY = dom.plotBackground.y();

    // viewport clip
    dom.viewportClip.x(refX).y(refY);

    // viewport
    dom.viewport.x(refX).y(refY);
    refX = metadata.axisX.plotOffset;

    if (modeIs(this, MODE_INTERACTIVE) && options.yAxisPosition === 'left') {
      refX += BASE_PADDING + metadata.axisY.width;
    }

    // plot area
    dom.plotArea.x(refX);

    // x-axis
    dom.axisX.x(refX).y(dom.plotArea.height());
    refY = !modeIs(this, MODE_INTERACTIVE)
      ? dom.viewport.y() + dom.viewportClipRect.height() + padding
      : dom.plotBackground.y() + dom.plotBackground.height() + padding;

    // chart footer
    if (dom.footer) {
      dom.footer.x(padding).y(refY);
    }

    // Fix viewport z-index (needs to be on top)
    dom.viewport.front();

    // Update chart height
    if (modeIs(this, MODE_ARTICLE)) {
      dom.chart.height(dom.chart.bbox().height + padding * 2);
    }

    // Cache position of plot area relative to browser viewport (used by tooltips)
    state.plotAreaOffset = dom.plotArea.node.getBoundingClientRect();

    return this;
  }

  /** Renders the header section of the chart (title, subtitle, etc)
   * @returns {LineChart} - The chart instance
   */
  drawHeader() {
    const { data, dom, state } = this;

    if (!state.headerShown || (!data.title && !data.subtitle)) {
      dom.header = null;
      return this;
    }

    dom.header = new EmbeddedSVG()
      .appendTo(dom.chart)
      .addClass(cssClasses.header);

    let title;
    if (data.title) {
      let wrappedTitle;
      if (modeIs(this, MODE_TILE)) {
        wrappedTitle = utils.wrapText(
          data.title.toUpperCase(),
          state.textWrap,
          SM_TEXT_FONT,
          0.1
        );
      } else {
        wrappedTitle = utils.wrapText(data.title, state.textWrap, TITLE_FONT);
      }

      title = new Text(wrappedTitle, TITLE_LEADING)
        .appendTo(dom.header)
        .addClass(cssClasses.title)
        .y(`-${(TITLE_LEADING - 1) / 2}em`); // to align text top to header top -- accounts for leading
    }

    if (data.subtitle) {
      const subtitle = new Text(
        utils.wrapText(data.subtitle, state.textWrap, SM_TEXT_FONT),
        SM_TEXT_LEADING
      )
        .appendTo(dom.header)
        .addClass(cssClasses.subtitle);

      if (title) {
        subtitle.y(title.bbox().y + title.bbox().height + BASE_PADDING);
      }
    }

    const bbox = dom.header.bbox();
    dom.header.attr({
      width: bbox.width,
      height: bbox.height,
    });

    return this;
  }

  /** Renders the chart legend
   * @returns {LineChart} - The chart instance
   */
  drawLegend() {
    const { data, dom, options, state } = this;

    if (!options.showLegend) {
      dom.legend = null;
      return this;
    }

    dom.legend = new EmbeddedSVG()
      .appendTo(dom.chart)
      .addClass(cssClasses.legend);

    let x = 0;
    let y = 0;
    data.lines.forEach((line, i) => {
      const g = new Group()
        .appendTo(dom.legend)
        .addClass(cssClasses.legend_item)
        .x(x)
        .y(y);

      new Path([
        { x: 0, y: 5 },
        { x: LEGEND_MARKER_SIZE, y: 5 },
      ])
        .appendTo(g)
        .addClass(cssClasses.line)
        .attr({
          fill: 'none',
          stroke: line.color,
          'data-line-id': line.id,
          'stroke-dasharray': line.strokeDashArray || '',
        });

      let label;
      if ('title' in line) {
        label = new Text(line.title)
          .appendTo(g)
          .x(LEGEND_MARKER_SIZE + BASE_PADDING / 2);
      }

      // Get width of legend item to see if wrapping is needed
      const { width } = g.bbox();

      // Wrap legend item onto its own line if it isn't already
      if (
        (x > 0 && x + width > state.textWrap) ||
        (options.forceMultilineLegend && i > 0)
      ) {
        x = 0;
        y = g.bbox().bottom + BASE_PADDING / 2;
        g.x(x).y(y);
      }

      // If it's already on its own line, word wrap the label
      if (x === 0 && x + width > state.textWrap && label) {
        label.text(
          utils.wrapText(line.title, state.textWrap - label.x(), SM_TEXT_FONT)
        );
      }

      x += width + BASE_PADDING;
    });

    const bbox = dom.legend.bbox();
    dom.legend.attr({
      width: bbox.width,
      height: bbox.height,
    });

    return this;
  }

  /** Renders the footer section of the chart (footnote, source, etc)
   * @returns {LineChart} - The chart instance
   */
  drawFooter() {
    const { data, options, dom, state } = this;

    if (options.showFooter === false || (!data.footnote && !data.source)) {
      dom.footer = null;
      return this;
    }

    dom.footer = new EmbeddedSVG()
      .appendTo(dom.chart)
      .addClass(cssClasses.footer);
    let bottomSpacing = 0;

    let footnote;
    if (data.footnote) {
      footnote = new Text(
        utils.wrapText(data.footnote, state.textWrap, SM_TEXT_FONT),
        SM_TEXT_LEADING
      )
        .appendTo(dom.footer)
        .addClass(cssClasses.footnote);
    }

    if (data.source) {
      const text = utils.wrapText(
        data.source.toUpperCase(),
        state.textWrap,
        SM_TEXT_FONT,
        0.1
      );

      // Hack to make the source on tile graphs have a fixed height -- DOTCOME-3149
      if (modeIs(this, MODE_TILE) && text.indexOf('\n') < 0) {
        bottomSpacing = 11;
      }

      const source = new Text(text)
        .appendTo(dom.footer)
        .addClass(cssClasses.source);

      if (footnote && !modeIs(this, MODE_TILE)) {
        source.y(footnote.bbox().y + footnote.bbox().height + BASE_PADDING);
      }
    }

    const bbox = dom.footer.bbox();
    dom.footer.attr({
      width: bbox.width,
      height: bbox.height + bottomSpacing,
    });

    return this;
  }

  /** Renders the chart plot area (chart background and viewport)
   * @returns {LineChart} - The chart instance
   */
  drawPlotArea() {
    const { metadata, state, dom, options } = this;
    const width =
      options.yAxisPosition !== 'hidden'
        ? Math.max(state.innerWidth - BASE_PADDING - metadata.axisY.width, 0)
        : state.innerWidth;
    const plotWidth = Math.max(
      width * state.zoomLevel - metadata.axisX.plotOffset * 2,
      0
    );
    let height;

    if (modeIs(this, MODE_ARTICLE)) {
      height = ARTICLE_MODE_PLOT_AREA_HEIGHT;
    } else {
      height = Math.max(
        state.innerHeight -
          // chart header
          (dom.header ? dom.header.height() + BASE_PADDING : 0) -
          // legend
          (dom.legend ? dom.legend.height() + BASE_PADDING : 0) -
          // x-axis
          metadata.axisX.height -
          // chart footer
          (dom.footer ? state.chartPadding + dom.footer.height() : 0),
        0
      );
    }

    const plotBackgroundWidth = !modeIs(this, MODE_INTERACTIVE)
      ? width
      : state.innerWidth;
    const plotBackgroundHeight =
      height + (modeIs(this, MODE_INTERACTIVE) ? metadata.axisX.height : 0);

    dom.plotBackground = new EmbeddedSVG()
      .appendTo(dom.chart)
      .addClass(cssClasses.plot_bg)
      .size(plotBackgroundWidth, plotBackgroundHeight);

    new Rect(0, 0)
      .appendTo(dom.plotBackground)
      .addClass(cssClasses.placeholder)
      .size(plotBackgroundWidth, plotBackgroundHeight);

    dom.viewportClip = new ClipPath();
    dom.chart.addDef(dom.viewportClip);

    dom.viewportClipRect = new Rect(0, 0)
      .appendTo(dom.viewportClip)
      .attr('fill', 'transparent')
      .size(width, height + metadata.axisX.height);

    if (modeIs(this, MODE_INTERACTIVE) && state.zoomLevel === 1) {
      dom.viewportClipRect2 = new Rect(0, 0)
        .appendTo(dom.viewportClip)
        .attr('fill', 'transparent')
        .size(plotBackgroundWidth, metadata.axisX.height)
        .y(height);
    }

    dom.chart.defs.appendRawSVG(`
      <linearGradient id="${this.zoomFadeId}">
        <stop offset="0" stop-color="#ecf4de" stop-opacity="0"/>
        <stop offset="0.1" stop-color="#ecf4de" stop-opacity="0"/>
        <stop offset="0.9" stop-color="#ecf4de" stop-opacity="0"/>
        <stop offset="1" stop-color="#ecf4de" stop-opacity="0"/>
      </linearGradient>
    `);
    dom.zoomFade = this.el.getElementById(this.zoomFadeId);

    dom.viewport = new EmbeddedSVG()
      .appendTo(dom.chart)
      .addClass(cssClasses.viewport)
      .clip(dom.viewportClip);

    dom.plotArea = new EmbeddedSVG()
      .appendTo(dom.viewport)
      .addClass(cssClasses.plot_area)
      .size(plotWidth, height);

    new Rect(plotWidth, height)
      .appendTo(dom.plotArea)
      .addClass(cssClasses.placeholder);

    const urlZoomFadeId = `url(#${this.zoomFadeId})`;

    dom.zoomOverlay = new Rect(width, height + metadata.axisX.height)
      .appendTo(dom.viewport)
      .attr('fill', urlZoomFadeId);

    this.updateZoomFade();
    return this;
  }

  /** Renders the data lines to the chart
   * @returns {LineChart} - The chart instance
   */
  drawLines() {
    const { data, metadata, options, dom } = this;
    const lines = new Group()
      .appendTo(dom.plotArea)
      .addClass(cssClasses.lines_group);

    data.lines.forEach((line, i) => {
      if (line.points.length < 2) return;

      let linePointsArray = line.points;
      let linePointsNewArray = [];

      for (let arr, i=0; i<linePointsArray.length; i++) {
        if (isNaN(linePointsArray[i].y)) {
          arr = null;
        }
        else {
          if (!arr) linePointsNewArray.push(arr=[]);
          arr.push(linePointsArray[i]);
        }
      }

      linePointsNewArray.forEach((pointArray) => {
        const points = pointArray.map((p) => ({
          x: plotX(this, p.x),
          y: plotY(this, p.y),
        }));

        new Path(points)
          .appendTo(lines)
          .addClass(cssClasses.line)
          .attr({
            fill: 'none',
            stroke: line.color,
            'data-line-id': line.id,
            'stroke-dasharray': line.strokeDashArray || '',
        });
      });
    });

    if (options.showPointLabels && options.pointLabels) {
      const pointLabelsGroup = new Group().appendTo(dom.viewport);

      const { pointLabels } = options;
      Object.keys(pointLabels).forEach((key) => {
        if (!(pointLabels[key] instanceof Array) || !pointLabels[key].length) {
          return;
        }

        const pos = pointLabels[key][0];
        const showOnTile = !!pointLabels[key][1];
        const isAnnotation = !!pointLabels[key][2] && !modeIs(this, MODE_TILE);
        const text = isAnnotation ? pointLabels[key][2] : '{y}';
        if (modeIs(this, MODE_TILE) && !showOnTile) return;

        const [lineIndex, x] = key.split(',').map((value) => Number(value));
        const line = data.lines[lineIndex];
        if (!line) return;
        const point = line.points.find((p) => p.x === x);
        if (!point) return;

        let top;
        let left;
        let anchor;
        const positions = {
          above: [-20, 0, 'middle'],
          below: [10, 0, 'middle'],
          left: [-5, -10, 'end'],
          right: [-5, 10, 'start'],
        };

        if (positions[pos]) {
          [top, left, anchor] = positions[pos];
        } else {
          [top, left] = pos.split(' ').map((value) => Number(value));
          anchor = 'middle';
        }

        // Get point position relative to viewport
        const pointPos = {
          x: Math.round(plotX(this, point.x)) + metadata.axisX.plotOffset,
          y: Math.round(plotY(this, point.y)),
        };
        const labelPos = { x: pointPos.x + left, y: pointPos.y + top };

        const value = text.replace(
          '{y}',
          utils.readableNumber(point.y, metadata.axisY.decimalPlaces)
        );
        const wrappedText = utils.wrapText(
          value,
          ANNOTATION_MAX_WIDTH,
          SM_TEXT_FONT
        );
        const label = new Text(wrappedText, SM_TEXT_LEADING)
          .appendTo(pointLabelsGroup)
          .addClass(cssClasses[isAnnotation ? 'annotation' : 'point_label'])
          .x(labelPos.x)
          .y(labelPos.y)
          .attr({
            'text-anchor': anchor,
            'data-key': key,
            'data-position': pos,
          });

        const bbox = label.bbox();
        const bboxRounded = {
          x: Math.round(bbox.x),
          y: Math.round(bbox.y),
          width: Math.round(bbox.width),
          height: Math.round(bbox.height),
        };

        const distance = utils.distanceToElement(
          pointPos.x,
          pointPos.y,
          bboxRounded.x,
          bboxRounded.y,
          bboxRounded.width,
          bboxRounded.height
        );

        if (distance > 10) {
          const anchorPos = utils.getAnchorPosition(
            pointPos.x,
            pointPos.y,
            labelPos.x,
            labelPos.y,
            bboxRounded.width,
            bboxRounded.height
          );

          new Line(pointPos.x, pointPos.y, anchorPos.x, anchorPos.y)
            .appendTo(pointLabelsGroup)
            .addClass(cssClasses.point_label_line);
        }

        if (isAnnotation) {
          new Rect(
            bboxRounded.width + BASE_PADDING * 2,
            bboxRounded.height + BASE_PADDING
          )
            .appendTo(pointLabelsGroup)
            .addClass(cssClasses.annotation_bg)
            .x(bboxRounded.x - BASE_PADDING)
            .y(bboxRounded.y - BASE_PADDING / 2);

          new Circle(DATA_POINT_RADIUS)
            .appendTo(pointLabelsGroup)
            .addClass(cssClasses.point)
            .cx(pointPos.x)
            .cy(pointPos.y)
            .attr('fill', line.color);

          label.front();
        }
      });
    }

    return this;
  }

  /** Renders the x-axis of the chart
   * @returns {LineChart} - The chart instance
   */
  drawXAxis() {
    const { dom, options } = this;
    const { axisX } = this.metadata;

    dom.axisX = new EmbeddedSVG()
      .appendTo(dom.viewport)
      .addClass(cssClasses.x_axis);

    if (!axisX.ticks) return this;

    // draw tick marks and labels
    let svg = '';
    if (!modeIs(this, MODE_INTERACTIVE)) {
      svg = axisX.ticks
        .map(
          (tick) => `
        <g class="${cssClasses.tick}" transform="translate(${plotX(
            this,
            tick.x
          )}, 0)">
          <line y2="${X_AXIS_TICK_MARK_HEIGHT}"/>
          <text y="${X_AXIS_TICK_MARK_HEIGHT + BASE_PADDING * 1.5}">${
            tick.text || ''
          }</text>
        </g>
      `
        )
        .join('');
    } else {
      const gridLines = new Group()
        .appendTo(dom.plotArea)
        .addClass(cssClasses.x_axis_grid_lines);

      let prevTickRight = 0;
      let prevTickTop;

      axisX.ticks.forEach((tick) => {
        const xScaled = plotX(this, tick.x);
        let y = 0;

        if (options.wrapAxisLabels) {
          if (prevTickRight > xScaled - 10 && prevTickTop === 0) {
            y = 15;
          }
          prevTickRight = xScaled + utils.measureText(tick.text || '');
          prevTickTop = y;
        }

        svg += `
          <g class="${cssClasses.tick}" transform="translate(${xScaled}, ${y})">
            <text x="${BASE_PADDING / 2}" y="${BASE_PADDING * 1.5}">${
          tick.text || ''
        }</text>
          </g>`;

        // draw tick line
        new Line(xScaled, 0, xScaled, dom.plotBackground.height())
          .appendTo(gridLines)
          .addClass(cssClasses.grid_line);
      });
    }

    dom.axisX.appendRawSVG(svg);

    const bbox = dom.axisX.bbox();
    dom.axisX.attr({
      width: bbox.width,
      height: bbox.height,
    });

    return this;
  }

  /** Renders the y-axis of the chart
   * @returns {LineChart} - The chart instance
   */
  drawYAxis() {
    const { metadata, dom } = this;

    dom.axisY = new EmbeddedSVG()
      .appendTo(dom.chart)
      .addClass(cssClasses.y_axis);

    if (!metadata.axisY.ticks) return this;

    const gridLines = new Group()
      .appendTo(dom.plotBackground)
      .addClass(cssClasses.y_axis_grid_lines);

    metadata.axisY.ticks.forEach((tick, index) => {
      const yScaled = plotY(this, tick.y);

      // draw tick label
      const g = new Group()
        .appendTo(dom.axisY)
        .addClass(cssClasses.tick)
        .x(metadata.axisY.width)
        .y(yScaled);

      if (!tick.hideLabel) {
        const label = new Text(tick.text).appendTo(g);

        if (!modeIs(this, MODE_INTERACTIVE)) {
          label.y('-0.5em');
        } else if (index === 0) {
          label.y(BASE_PADDING * -1.5);
        } else {
          label.y(BASE_PADDING / 2);
        }
      }

      // draw tick line
      new Line(
        0,
        yScaled,
        dom.plotBackground.x() + dom.plotBackground.width(),
        yScaled
      )
        .appendTo(gridLines)
        .addClass(tick.y === 0 ? cssClasses.axis_line : cssClasses.grid_line);

      if (tick.y === 0 && index === 0) {
        dom.axisX.addClass(cssClasses.axis_at_zero);
      }
    });

    return this;
  }

  /** Creates tooltip elements and stores references to them
   * @returns {LineChart} - The chart instance
   */
  drawTooltips() {
    const { dom, data, metadata } = this;

    dom.groupTooltip = document.createElement('div');
    const gt = dom.groupTooltip;
    gt.className = `${cssClasses.tooltip} ${cssClasses.group_tooltip}`;
    overlay.add(gt, this.el.id);

    dom.groupTooltipLabel = document.createElement('p');
    const gtLabel = dom.groupTooltipLabel;
    gtLabel.className = cssClasses.tooltip_label;
    gtLabel.innerHTML = '&nbsp;';
    gt.appendChild(gtLabel);

    dom.groupTooltipLines = [];
    dom.tooltips = [];
    metadata.tooltips = [];
    dom.tooltipPoints = new Group().appendTo(dom.viewport).x(dom.plotArea.x());

    data.lines.forEach((line) => {
      const tooltip = document.createElement('div');
      tooltip.className = cssClasses.tooltip;
      tooltip.innerHTML = `
        <span class="${cssClasses.tooltip_label}">&nbsp;</span>
        <span class="${cssClasses.tooltip_value}">&nbsp;</span>
      `;
      overlay.add(tooltip, this.el.id);
      dom.tooltips.push(tooltip);

      if (!metadata.tooltipHeight) {
        metadata.tooltipHeight = tooltip.clientHeight;
      }
      tooltip.style.marginTop = `-${metadata.tooltipHeight + 10}px`;

      const gtLine = document.createElement('p');
      gtLine.innerHTML = `
        <span class="${cssClasses.tooltip_value}">&nbsp;</span>
        <span class="${cssClasses.tooltip_line_label}">&nbsp;</span>
      `;
      gt.appendChild(gtLine);
      dom.groupTooltipLines.push(gtLine);

      new Circle(DATA_POINT_RADIUS)
        .appendTo(dom.tooltipPoints)
        .addClass(`${cssClasses.point} ${cssClasses.tooltip_point}`)
        .attr({
          fill: line.color,
          style: 'visibility: hidden;',
        });
    });

    gt.style.marginTop = `-${gt.clientHeight + 10}px`;

    return this;
  }

  /** Gets information needed to render tooltips (position, text contents)
   * @returns {Object} - The tooltip metadata
   */
  getTooltipMetadata(plotAreaX) {
    const { options, metadata, data, state } = this;
    let commonX;
    if (options.strictModeTooltips) {
      commonX = findX(this, plotAreaX);
      metadata.lastTooltipXValue = commonX;
    }

    let updateNeeded = false;
    let minX = Infinity;
    let maxX = -Infinity;
    let gtLeft = 0;
    let gtTop = Infinity;
    let gtLabel;

    for (let i = 0; i < data.lines.length; i++) {
      const line = data.lines[i];
      const x = commonX || findX(this, plotAreaX, i);
      const pointIndex = metadata.axisX.valuesByLine[i].indexOf(x);

      if (pointIndex === -1) {
        metadata.tooltips[i] = false;
      } else {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;

        if (!metadata.tooltips[i] || metadata.tooltips[i].x !== x) {
          updateNeeded = true;
          const { y } = line.points[pointIndex];
          metadata.tooltips[i] = { x, y };

          const value = options.unitFormat
            ? options.unitFormat.replace(
                '{y}',
                utils.readableNumber(y, metadata.axisY.decimalPlaces)
              )
            : utils.readableNumber(y, metadata.axisY.decimalPlaces);

          let label = getLabelForX(this, x);
          if (options.tooltipLabel) {
            label = options.tooltipLabel(line, x, label);
          }

          const lineLabel = options.tooltipLineLabel
            ? options.tooltipLineLabel(line, x, label)
            : line.title;

          const estWidth =
            utils.measureText(label.toUpperCase(), SM_TEXT_FONT) +
            8 +
            value.length * 6.5 +
            20;
          const plottedX = plotX(this, x);
          const plottedY = plotY(this, y);
          const left = Math.round(state.plotAreaOffset.left + plottedX);
          const top = Math.round(
            window.pageYOffset + state.plotAreaOffset.top + plottedY
          );

          let position = 'top';
          if (left - estWidth / 2 < 0) {
            position = 'right';
          } else if (left + estWidth / 2 > window.innerWidth) {
            position = 'left';
          }

          const rect = {
            top,
            left,
            bottom: top + metadata.tooltipHeight + 15,
            right: left + estWidth,
          };

          Object.assign(metadata.tooltips[i], {
            label,
            lineLabel,
            value,
            plottedX,
            plottedY,
            rect,
            position,
            color: line.color,
          });
        }

        if (metadata.tooltips[i] && metadata.tooltips[i].rect.top < gtTop) {
          gtTop = metadata.tooltips[i].rect.top;
          gtLeft = metadata.tooltips[i].rect.left;
        }
      }
    }

    const minLabel = getLabelForX(this, minX);
    if (minX < maxX) {
      const maxLabel = getLabelForX(this, maxX);
      gtLabel = options.groupTooltipLabel
        ? options.groupTooltipLabel(minLabel, maxLabel)
        : `${minLabel}&ndash;${maxLabel}`;
    } else {
      gtLabel = options.groupTooltipLabel
        ? options.groupTooltipLabel(minLabel)
        : minLabel;
    }

    return {
      updateNeeded,
      minX,
      maxX,
      gtLeft,
      gtTop,
      gtLabel,
    };
  }

  /** Sets the position and text content of tooltips based on the cursor position
   * @returns {LineChart} - The chart instance
   */
  updateTooltips(clientX) {
    const { state, metadata, dom } = this;
    if (!dom.tooltips || !dom.tooltips.length) return this;
    const mdata = this.getTooltipMetadata(clientX - state.plotAreaOffset.left);

    if (!mdata.updateNeeded) {
      if (state.viewportHover) {
        document.documentElement.classList.add(cssClasses.chart_viewport_hover);
      }
      return this;
    }

    let tooltipsOverlap = false;
    for (let i = 0; i < metadata.tooltips.length; i++) {
      const a = metadata.tooltips[i];
      for (let j = i + 1; j < metadata.tooltips.length; j++) {
        const b = metadata.tooltips[j];
        if (a && b && utils.rectsOverlap(a.rect, b.rect, BASE_PADDING)) {
          tooltipsOverlap = true;
        }
      }
    }

    const positionConfig = {
      top: {
        cls: '',
        translate: 'translateX(-50%)',
      },
      left: {
        cls: cssClasses.tooltip_left,
        translate: 'translateX(calc(-100% + 10px))',
      },
      right: {
        cls: cssClasses.tooltip_right,
        translate: 'translateX(-10px)',
      },
    };

    if (mdata.gtTop === Infinity) return;

    domWrite(() => {
      if (!tooltipsOverlap) {
        for (let i = 0; i < dom.tooltips.length; i++) {
          const tooltip = dom.tooltips[i];
          const point = dom.tooltipPoints.child(i);
          const info = metadata.tooltips[i];
          if (info) {
            const { cls, translate } = positionConfig[info.position];

            tooltip.className = `${cssClasses.tooltip} ${cls}`;
            tooltip.innerHTML = `
              <span class="${cssClasses.tooltip_label}">${info.label}</span>
              <span class="${cssClasses.tooltip_value}" style="color: ${info.color}">${info.value}</span>
            `;
            tooltip.style.transform = supports3dTransforms
              ? `translate3d(${info.rect.left}px, ${info.rect.top}px, 0) ${translate}`
              : `translate(${info.rect.left}px, ${info.rect.top}px ${translate}`;
            tooltip.style.visibility = 'visible';

            point.setAttribute('cx', info.plottedX);
            point.setAttribute('cy', info.plottedY);
            point.style.visibility = 'visible';
          } else if (info === false) {
            tooltip.style.visibility = 'hidden';
            point.style.visibility = 'hidden';
          }
        }

        dom.groupTooltip.style.visibility = 'hidden';
      } else {
        for (let i = 0; i < dom.groupTooltipLines.length; i++) {
          const point = dom.tooltipPoints.child(i);
          const info = metadata.tooltips[i];
          if (info && info.plottedY) {
            dom.groupTooltipLines[i].innerHTML = `
              <span class="${cssClasses.tooltip_value}" style="color: ${info.color}">${info.value}</span>
              <span class="${cssClasses.tooltip_line_label}">${info.lineLabel}</span>
            `;
          }

          dom.tooltips[i].style.visibility = 'hidden';
          if (info.plottedY) {
            point.setAttribute('cx', info.plottedX);
            point.setAttribute('cy', info.plottedY);
            point.style.visibility = 'visible';
          }
        }

        const { cls, translate } = positionConfig[
          metadata.tooltips[0].position
        ];
        dom.groupTooltipLabel.innerHTML = mdata.gtLabel;
        dom.groupTooltip.className = `${cssClasses.tooltip} ${cssClasses.group_tooltip} ${cls}`;
        dom.groupTooltip.style.transform = supports3dTransforms
          ? `translate3d(${mdata.gtLeft}px, ${mdata.gtTop}px, 0) ${translate}`
          : `translate(${mdata.gtLeft}px, ${mdata.gtTop}px) ${translate}`;
        dom.groupTooltip.style.visibility = 'visible';
      }

      if (state.viewportHover) {
        document.documentElement.classList.add(cssClasses.chart_viewport_hover);
      }
    });

    this.emit('tooltipsupdated');
    return this;
  }

  /** Changes the data set rendered by the chart
   * @param {Object} newData - The data object to chart
   * @param {Boolean} overwrite - Whether to overwrite the existing data object (or add to it)
   * @returns {LineChart} - The chart instance
   */
  changeData(newData, overwrite = false) {
    if (this.options.enableValidation) {
      const isValid = validateData(newData).valid;
      if (!isValid && this.options.strictValidation) return this;
    }

    this.data = normalizeData(
      overwrite ? newData : Object.assign({}, this.data, newData)
    );
    this.redraw(true).emit('datachanged');
    return this;
  }

  /** Adds a line to the existing data set
   * @param {Object} line - The line object to add
   * @returns {LineChart} - The chart instance
   */
  addLine(line) {
    if (this.data.lines.length < this.state.maxLines) {
      this.changeData({ lines: [...this.data.lines, line] });
    }
    return this;
  }

  /** Removes a line from the data set
   * @param {Number} lineIndex - The index of the line to remove
   * @returns {LineChart} - The chart instance
   */
  removeLine(lineIndex) {
    this.changeData({
      lines: this.data.lines.filter((line, index) => index !== lineIndex),
    });
    return this;
  }

  /** Changes chart options and rerenders
   * @param {Object} newOptions - The options object
   * @param {Boolean} overwrite - Whether to overwrite the existing options object (or extend it)
   * @returns {LineChart} - The chart instance
   */
  changeOptions(newOptions, overwrite = false) {
    this.options = Object.assign(
      {},
      overwrite ? DEFAULT_OPTIONS : this.options,
      newOptions
    );
    this.destroy().init().emit('optionschanged');
    return this;
  }

  /** Rerenders the chart
   * @param {Boolean} refreshMetadata - Whether to recalculate metadata before rerendering (if data has changed)
   * @returns {LineChart} - The chart instance
   */
  redraw(refreshMetadata = false) {
    if (refreshMetadata === true) {
      this.createMetadata();
    }

    overlay.removeAllByOwner(this.el.id);
    this.clearSvg().draw().emit('redraw');
    return this;
  }

  /** Updates the chart viewport to a given left position
   * @param {Number} x - The left (or x) position to scroll to
   * @returns {LineChart} - The chart instance
   */
  scrollTo(x) {
    const { dom } = this;
    const offset = -x;
    dom.plotArea.x(offset);
    dom.tooltipPoints.x(offset);
    dom.axisX.x(offset);
    this.state.viewportOffset = offset;
    this.updateZoomFade();
    return this;
  }

  /** Updates the chart viewport to a given x-value
   * @param {Number} x - The x-value to scroll to
   * @returns {LineChart} - The chart instance
   */
  scrollToPoint(x) {
    this.scrollTo(plotX(this, x) - this.metadata.axisX.plotOffset);
    return this;
  }

  /** Sets the zoom level of the chart
   * @param {Number} zoomLevel - A multiplier to set the zoom to
   * @returns {LineChart} - The chart instance
   */
  zoom(zoomLevel) {
    if (modeIs(this, MODE_INTERACTIVE, MODE_CUSTOM)) {
      this.changeOptions({ zoomLevel });
    }
    return this;
  }

  /** Zooms to a given range of x-values
   * @param {Number} start - The start x-value
   * @param {Number} end - The number of points to span
   * @returns {LineChart} - The chart instance
   */
  zoomToRange(start, end = this.data.labels.length) {
    const { labels } = this.data;
    const { axisX } = this.metadata;
    const rangeStart =
      start >= 0
        ? Math.min(start, labels.length - 1)
        : Math.max(labels.length + start, 0);
    const rangeEnd =
      end >= 0
        ? Math.min(end, labels.length - 1)
        : Math.max(labels.length + end, 0);
    const range = Math.max(rangeEnd - rangeStart, 1);
    const startPoint = labels[rangeStart].x;
    const endPoint = labels[rangeEnd].x;

    // Do an initial zoom -- necessary to make plotX() return accurate values
    this.zoom(axisX.range / range);

    const pixelRange =
      plotX(this, endPoint) - plotX(this, startPoint) + axisX.plotOffset * 2;
    this.zoom(this.dom.viewport.bbox().width / pixelRange);
    this.scrollToPoint(startPoint);
    return this;
  }

  /** Zooms to a given range of days if labels were provided as dates
   * @param {Number} numDays - The number of days to display at a time
   * @returns {LineChart} - The chart instance
   */
  zoomTimePeriod(numDays) {
    if (
      this.data.labelType === 'date' ||
      this.data.labelType === 'date_range'
    ) {
      const d = new Date();
      d.setDate(d.getDate() + numDays);
      this.zoom(this.metadata.axisX.range / (d - new Date()));
    }
    return this;
  }

  /** Updates the fading opacity around the viewport when zoomed
   * @returns {LineChart} - The chart instance
   */
  updateZoomFade() {
    const { dom } = this;
    const fadeStart = dom.plotArea.x() < 0;
    const fadeEnd =
      dom.plotArea.x() > dom.viewportClipRect.width() - dom.plotArea.width();
    dom.zoomFade.setAttribute(
      'class',
      (fadeStart ? 'fade-start ' : '') + (fadeEnd ? 'fade-end' : '')
    );
    dom.zoomOverlay.front();
    return this;
  }

  /** Given a point (x, y) on the chart, returns the position in pixels
   * @param {Object} point - The point in { x, y } format
   * @returns {Object} - The position in pixels { left, top }
   */
  getPointPosition(point) {
    return { left: plotX(this, point.x), top: plotY(this, point.y) };
  }

  /** Embeds the LineChart styles in the <svg> element for exporting as a static image
   * @returns {LineChart} - The chart instance
   */
  embedStyles() {
    const { dom } = this;
    // Embed line chart styles inside SVG element
    dom.stylesheet = document.createElement('style');
    // Scope it to this particular SVG element
    dom.stylesheet.innerHTML = COMPONENT_CSS.replace(
      new RegExp(`.${cssClasses.chart}`, 'g'),
      `#${this.el.id}`
    );
    dom.chart.appendChild(dom.stylesheet);

    return this;
  }

  /** Removes all children of the root <svg> element
   * @param {Boolean} keepStyles - Whether to keep embedded styles or not
   * @returns {LineChart} - The chart instance
   */
  clearSvg(keepStyles = true) {
    const { dom } = this;
    dom.chart.removeAllChildren();

    // Re-add stylesheet element to SVG
    if (keepStyles && dom.stylesheet) {
      dom.chart.appendChild(dom.stylesheet);
    }

    return this;
  }

  /** Removes all chart elements from the DOM and clears internal state
   * @returns {LineChart} - The chart instance
   */
  destroy() {
    this.emit('destroy');

    // Clean up DOM elements
    this.clearSvg(false);
    this.dom.chart.remove();
    overlay.removeAllByOwner(this.el.id);

    // Clear references to DOM elements
    this.dom = { chart: this.dom.chart };
    // Clear state objects
    this.metadata = {};
    this.state = {};

    activeInstances.splice(activeInstances.indexOf(this), 1);
    return this;
  }

  /*
   ** Event Handlers
   */

  /** Handles `resize` on the window
   * @param {Boolean} force - Whether to force an update even if the chart size is unchanged
   */
  resize(force = false) {
    const { metadata, options, state, dom } = this;
    const rect = this.el.getBoundingClientRect();
    if (rect.width !== state.svgRect.width || force === true) {
      state.svgRect = rect;
      state.innerWidth = rect.width - state.chartPadding * 2;
      state.innerHeight = rect.height - state.chartPadding * 2;
      // Reset tooltip cache to force update -- window resize could affect tooltip positioning
      state.tooltipCachedCursorX = null;

      // Update displayed ticks unless we have user-defined ticks
      if (!options.ticks) {
        metadata.axisX.ticks = getXAxisTicks(this);
      }

      this.redraw().emit('resize');
    }

    // Cache position of plot area relative to browser viewport (used by tooltips)
    state.plotAreaOffset = dom.plotArea.node.getBoundingClientRect();
  }

  /** Handles `scroll` on the window
   */
  scroll() {
    const { dom, state } = this;
    state.svgRect = this.el.getBoundingClientRect();
    // Cache position of plot area relative to browser viewport (used by tooltips)
    state.plotAreaOffset = dom.plotArea.node.getBoundingClientRect();
  }

  /** Updates the chart viewport to a given left position
   * @param {Number} viewportLeft - The x position (relative to the viewport) to scroll to
   * @param {Event} e - The scroll event
   */
  scrollViewport(viewportLeft, e = this.state.dragLastEvent) {
    const { state, dom } = this;
    dom.plotArea.x(viewportLeft);
    dom.tooltipPoints.x(viewportLeft);
    dom.axisX.x(viewportLeft);
    this.updateZoomFade();

    if (state.tooltipsEnabled && e) {
      state.plotAreaOffset = dom.plotArea.node.getBoundingClientRect();
      this.updateTooltips(e.clientX);
    }
  }

  /** Scrolls the viewport based on an exponential decay to give scrolling momentum
   */
  autoScroll() {
    const { state } = this;
    state.autoScrolling = true;
    state.lastAutoScrollID = state.autoScrollID;
    if (state.dragAmplitude) {
      const elapsed = Date.now() - state.dragLastTime;
      const delta = -state.dragAmplitude * Math.exp(-elapsed / KINETIC);
      if (delta > 1 || delta < -1) {
        this.scrollViewport(state.dragAutoTarget + delta);
        state.autoScrollID = `autoScroll${Math.random()}`;
        nonconcurrentAF.queue(() => {
          nonconcurrentAF.cancel(state.lastAutoScrollID);
          this.autoScroll();
        }, this.state.autoScrollID);
      } else {
        this.scrollViewport(state.dragAutoTarget);
        state.autoScrolling = false;
      }
    } else {
      state.autoScrolling = false;
    }
  }

  /** Resets scroll velocity
   */
  resetVelocityTracker() {
    const { dom, state } = this;
    state.autoScrolling = false;
    nonconcurrentAF.cancel(state.autoScrollID);
    nonconcurrentAF.cancel(state.lastAutoScrollID);
    this.stopVelocityTracker();
    state.autoScrolling = false;
    state.dragVelocity = 0;
    state.dragAmplitude = 0;
    state.dragLastX = dom.plotArea.x();
    state.dragLastTime = Date.now();
  }

  /** Begins tracking scroll velocity as the user pans
   */
  startVelocityTracker() {
    this.resetVelocityTracker();
    dragTicker = setInterval(() => this.trackVelocity(), 100);
  }

  /** Stops tracking scroll velocity
   */
  stopVelocityTracker() {
    this.state.dragVelocity = 0;
    clearInterval(dragTicker);
  }

  /** Begins decaying scroll (momentum effect)
   */
  startAutoScroll() {
    const { dom, state } = this;
    if (Math.abs(state.dragVelocity) > 10 && !state.autoScrolling) {
      nonconcurrentAF.cancel(PANNING_UPDATE);
      state.dragAmplitude = 0.8 * state.dragVelocity;
      this.stopVelocityTracker();
      // Where we should end up
      state.dragAutoTarget = this.clampViewportX(
        dom.plotArea.x() + state.dragAmplitude
      );
      state.autoScrollID = `autoScroll${Math.random()}`;
      nonconcurrentAF.queue(this.autoScroll.bind(this), state.autoScrollID);
    }
  }

  /** Bounds a given viewport offset to minimum and maximum values
   * @param {Number} viewportLeft - The viewport offset
   * @returns {Number} - The bounded viewport offset
   */
  clampViewportX(viewportLeft) {
    const { metadata, dom } = this;
    return Math.max(
      Math.min(viewportLeft, metadata.axisX.plotOffset),
      dom.viewportClipRect.width() -
        dom.plotArea.width() -
        metadata.axisX.plotOffset
    );
  }

  /** Creates a rolling average of velocity based on the change of the viewport's x position
   */
  trackVelocity() {
    const { dom, state } = this;
    if (!state.panning) return;
    const viewportLeft = dom.plotArea.x();
    const timeSince = Date.now() - (state.dragLastTime || Date.now());
    const dragMoved = viewportLeft - (state.dragLastX || 0);
    const v = (1000 * dragMoved) / (1 + timeSince);
    state.dragVelocity = 0.8 * v + 0.2 * state.dragVelocity;
    state.dragLastTime = Date.now();
    state.dragLastX = viewportLeft;
  }

  /** Handles `mouseenter` on the root <svg> element
   */
  mouseEnter() {
    const { state, dom } = this;
    state.svgRect = this.el.getBoundingClientRect();
    state.plotAreaOffset = dom.plotArea.node.getBoundingClientRect();
  }

  /** Handles `mousemove` on the root <svg> element
   */
  mouseMove(e) {
    const { state, dom } = this;
    // Cursor position relative to chart SVG
    const cursor = {
      x: e.clientX - state.svgRect.left,
      y: e.clientY - state.svgRect.top,
    };
    const inViewport = utils.inBounds(
      cursor.x,
      cursor.y,
      dom.viewport.x(),
      dom.viewport.y(),
      dom.viewport.x() + dom.viewportClipRect.width(),
      dom.viewport.y() + dom.viewportClipRect.height()
    );

    state.viewportHover = inViewport;

    if (inViewport || modeIs(this, MODE_INTERACTIVE, MODE_CUSTOM)) {
      if (state.tooltipsEnabled && !state.leftMouseDown) {
        this.state.dragLastEvent = e; // Track mouse event for use in autoScroll where we don't have an event any more
        nonconcurrentAF.replace(() => {
          this.updateTooltips(e.clientX);
        }, TOOLTIP_UPDATE);
      } else if (state.zoomLevel !== 1) {
        state.panning = true;
        dom.chart.addClass(cssClasses.chart_panning);
        state.dragLastEvent = e; // Track mouse event for use in autoScroll where we don't have an event any more
        nonconcurrentAF.queue(() => {
          const viewportLeft = this.clampViewportX(
            cursor.x - state.viewportOffset
          );
          this.scrollViewport(viewportLeft, e);
        }, PANNING_UPDATE);
      }
    }

    if (!inViewport) {
      nonconcurrentAF.cancel(TOOLTIP_UPDATE);
      document.documentElement.classList.remove(
        cssClasses.chart_viewport_hover
      );
    }
  }

  /** Handles `touchmove` on the root <svg> element
   */
  touchMove(e) {
    this.mouseMove({
      clientX: e.touches[0].clientX,
      clientY: e.touches[0].clientY,
    });
  }

  /** Handles `mouseleave` on the root <svg> element
   */
  mouseLeave(e) {
    // Stop tracking velocity, if the velocity is meaningful, keep scrolling using autoScroll
    if (
      modeIs(this, MODE_INTERACTIVE, MODE_CUSTOM) &&
      this.state.zoomLevel !== 1 &&
      this.state.leftMouseDown
    ) {
      this.state.dragLastEvent = e;
      this.startAutoScroll();
    }
  }

  /** Handles `mousedown` on the root <svg> element
   */
  mouseDown(e) {
    const { state, dom } = this;
    if (e.button === 0) {
      // left mouse button
      state.leftMouseDown = true;
      // Start tracking the velocity of any graph panning changes
      if (
        modeIs(this, MODE_INTERACTIVE, MODE_CUSTOM) &&
        state.zoomLevel !== 1
      ) {
        this.startVelocityTracker();
      }
    }
    state.viewportOffset = utils.mouseOffset(e, this.el).x - dom.plotArea.x();
  }

  /** Handles `touchstart` on the root <svg> element
   */
  touchStart(e) {
    this.state.leftMouseDown = true;
    this.mouseDown({
      clientX: e.touches[0].clientX,
      clientY: e.touches[0].clientY,
    });
  }

  /** Handles `mouseup` on the root <svg> element
   */
  mouseUp(e) {
    const { state, dom } = this;
    if (e.button === 0) {
      // left mouse button
      state.leftMouseDown = false;
    }
    state.panning = false;

    // Stop tracking velocity, if the velocity is meaningful, keep scrolling using autoScroll
    if (modeIs(this, MODE_INTERACTIVE, MODE_CUSTOM) && state.zoomLevel !== 1) {
      state.dragLastEvent = e;
      this.startAutoScroll();
    }
    dom.chart.removeClass(cssClasses.chart_panning);
  }

  /** Handles `touchend` on the root <svg> element
   */
  touchEnd() {
    this.mouseUp({ button: 0 });
  } 

  /** Handles 'focus' on the viewport <svg> element
   */
  focusStart() {
    const { state } = this;
    state.viewportFocused = true;
    if (!state.viewportHover && state.tooltipsEnabled) {
      state.viewportHover = true;
      state.focusIndex = 0;
      const clientX = findClientX(this, 0);
      nonconcurrentAF.replace(() => {
          this.updateTooltips(clientX);
        }, TOOLTIP_UPDATE);
    }
  }

  /** Handles 'blur' on the viewport <svg> element
   */
   focusEnd() {
    const { state } = this;
    state.viewportFocused = false;
    state.viewportHover = false;
  }

  /** Handles 'keydown' on the viewport <svg> element
   */
   keyDown(e) {
    const { state } = this;
    const { axisX } = this.metadata;
    if (state.viewportFocused) {
      let tempIndex;
      if (e.key === 'ArrowLeft') {
        tempIndex = state.focusIndex - 1;
      } else if (e.key === 'ArrowRight') {
        tempIndex = state.focusIndex + 1;
      }
      state.focusIndex = (((tempIndex) % axisX.values.length) + axisX.values.length) % axisX.values.length;
      const clientX = findClientX(this, state.focusIndex);
      nonconcurrentAF.replace(() => {
          this.updateTooltips(clientX);
        }, TOOLTIP_UPDATE);
    }
  }
}

// Expose some useful constants on LineChart class
Object.assign(LineChart, {
  MODE_ARTICLE,
  MODE_TILE,
  MODE_INTERACTIVE,
  MODE_CUSTOM,
});

/*
 ** Global Event Listeners
 */

window.addEventListener('resize', () => {
  nonconcurrentAF.queue(() => {
    activeInstances.forEach((instance) => instance.resize());
  }, RESIZE_UPDATE);
});

window.addEventListener('scroll', () => {
  nonconcurrentAF.queue(() => {
    activeInstances.forEach((instance) => instance.scroll());
  }, SCROLL_UPDATE);
});

window.addEventListener('blur', () => {
  document.documentElement.classList.remove(cssClasses.chart_viewport_hover);
});

document.addEventListener('mouseup', (e) => {
  activeInstances.forEach((instance) => {
    if (e.button === 0) {
      // left mouse button
      instance.state.leftMouseDown = false;
    }
  });
});

document.addEventListener('DOMContentLoaded', () => {
  document.body.addEventListener('typekitLoaded', () => {
    utils.clearTextMeasureDictionary();
    activeInstances.forEach((instance) => instance.redraw(true));
  });
});

export default LineChart;
